/**
 * CourierRatesGrouped — Checkout shipping rates dengan group by category.
 *
 * Backend (shop_routes /api/shipping/rates) annotate setiap rate dengan:
 *   { category: "instant"|"sameday"|"nextday"|"regular"|"economy"|"other",
 *     recommended: bool, priority: number }
 *
 * Komponen ini hanya bertugas:
 *  - Pisahkan rates rekomendasi → tampilkan section "⭐ Rekomendasi" di atas
 *  - Group sisa berdasarkan category dengan label customer-friendly
 *  - Tampilkan radio dengan format "Nama Kurir · Service · Rp · ETA"
 */
import React from "react";
import { Sparkles, Zap, Clock, Truck, Package, Star, Store } from "lucide-react";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { formatRp } from "@/lib/api";
import { formatEtd } from "@/lib/etd";

const CATEGORY_META = {
  store: { label: "Pengiriman Toko", icon: Store, color: "text-teal-700" },
  instant: { label: "Instant", icon: Zap, color: "text-blue-600" },
  sameday: { label: "Same Day", icon: Sparkles, color: "text-purple-600" },
  nextday: { label: "Next Day", icon: Clock, color: "text-amber-600" },
  regular: { label: "Regular", icon: Package, color: "text-emerald-600" },
  economy: { label: "Economy", icon: Truck, color: "text-slate-600" },
  other: { label: "Lainnya", icon: Truck, color: "text-gray-600" },
};

const CATEGORY_ORDER = ["store", "instant", "sameday", "nextday", "regular", "economy", "other"];

function groupRates(rates) {
  const recommended = [];
  const byCategory = new Map();
  rates.forEach((r, idx) => {
    const entry = { rate: r, idx };
    if (r.recommended) {
      recommended.push(entry);
      return;
    }
    const cat = r.category || "other";
    if (!byCategory.has(cat)) byCategory.set(cat, []);
    byCategory.get(cat).push(entry);
  });
  return { recommended, byCategory };
}

function RateOption({ entry, selectedRate, onSelect }) {
  const r = entry.rate;
  const idx = entry.idx;
  const id = `idx-${idx}`;
  const selected = selectedRate === r;
  const etdLabel = formatEtd(r.etd || r.eta);
  return (
    <Label
      className={`flex items-center justify-between gap-2 p-3 rounded-xl border cursor-pointer ${selected ? "border-[#111827] system-soft-bg" : "border-[#E5E5E5]"}`}
    >
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <RadioGroupItem value={id} data-testid={`rate-${id}`} />
        <div className="min-w-0">
          <div className="text-sm font-bold flex flex-wrap items-center gap-1" data-testid={`rate-name-${idx}`}>
            {r.recommended && <Star size={12} className="text-amber-500 fill-amber-500 shrink-0" />}
            <span className="truncate">
              {r.courier_name}
              <span className="text-[10px] uppercase tracking-wider text-[#7A5440] ml-1">
                · {r.courier_service_name || r.service}
              </span>
            </span>
          </div>
          <div className="text-xs text-[#5C5C5C]">
            {r.cost === 0 ? <span className="font-bold text-[#2D6A4F]">Gratis</span> : formatRp(r.cost)}
            {etdLabel ? ` · Estimasi tiba: ${etdLabel}` : ""}
          </div>
          {r.note ? <div className="text-[11px] text-[#8A8A8A] mt-0.5" data-testid={`rate-note-${idx}`}>{r.note}</div> : null}
        </div>
      </div>
    </Label>
  );
}

function CategorySection({ title, icon: Icon, color, testid, entries, selectedRate, onSelect }) {
  if (!entries || entries.length === 0) return null;
  return (
    <div className="space-y-2" data-testid={testid}>
      <div className={`flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider ${color}`}>
        <Icon size={12} /> {title}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {entries.map((e) => (
          <RateOption
            key={`opt-${e.idx}`}
            entry={e}
            selectedRate={selectedRate}
            onSelect={onSelect}
          />
        ))}
      </div>
    </div>
  );
}

export default function CourierRatesGrouped({ rates, selectedRate, onSelect }) {
  const { recommended, byCategory } = groupRates(rates);

  const value = selectedRate ? `idx-${rates.indexOf(selectedRate)}` : "";
  const handleChange = (v) => {
    const idx = Number(String(v).replace(/^idx-/, ""));
    const r = rates[idx];
    if (r) onSelect(r);
  };

  return (
    <RadioGroup
      value={value}
      onValueChange={handleChange}
      className="space-y-4"
      data-testid="rates-list"
    >
      <CategorySection
        title="Rekomendasi"
        icon={Star}
        color="text-amber-600"
        testid="rates-section-recommended"
        entries={recommended}
        selectedRate={selectedRate}
        onSelect={onSelect}
      />

      {CATEGORY_ORDER.map((cat) => {
        const meta = CATEGORY_META[cat];
        return (
          <CategorySection
            key={cat}
            title={meta.label}
            icon={meta.icon}
            color={meta.color}
            testid={`rates-section-${cat}`}
            entries={byCategory.get(cat)}
            selectedRate={selectedRate}
            onSelect={onSelect}
          />
        );
      })}
    </RadioGroup>
  );
}
